import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import { cn } from "@lawline/ui/core/cn";

import { authClient } from "@platform/auth/client";
import { pageSession } from "@platform/core/page-session";
import {
	sessionMutationOptions,
	useSessionTransition,
} from "@platform/auth/session-transition.context";

interface TenantExitActionProps {
	namespace: "tenant.leave" | "tenant.delete";
	hidden: boolean;
	run: (
		organizationId: string,
	) => Promise<{ data: { id: string } | null; error: { message?: string } | null }>;
	className?: string;
}

export function TenantExitAction({ namespace, hidden, run, className }: TenantExitActionProps) {
	const t = useTranslations(namespace);
	const changeSession = useSessionTransition();
	const { data: tenant } = authClient.useActiveOrganization();
	const { data: tenants } = authClient.useListOrganizations();

	if (!tenant || hidden) return null;

	async function exitTenant() {
		if (!tenant) return;
		await changeSession(async () => {
			const { data, error } = await run(tenant.id);
			if (error || !data?.id) throw new Error("Organization exit is unconfirmed");
			const next = tenants?.find((organization) => organization.id !== tenant.id);
			if (next) {
				const { data: active, error: activeError } = await authClient.organization.setActive({
					organizationId: next.id,
					fetchOptions: {
						...sessionMutationOptions,
						headers: pageSession.afterTenantExitHeaders(),
					},
				});
				if (activeError || active?.id !== next.id)
					throw new Error("Organization switch is unconfirmed");
			}
			return "reload";
		});
	}

	return (
		<section className={cn("flex flex-col gap-2", className)}>
			<AlertDialog>
				<AlertDialogTrigger asChild>
					<Button variant="destructive" className="w-fit">
						{t("trigger")}
					</Button>
				</AlertDialogTrigger>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("title", { name: tenant.name })}</AlertDialogTitle>
						<AlertDialogDescription>{t("description")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
						<AlertDialogAction variant="destructive" onClick={exitTenant}>
							{t("confirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</section>
	);
}
